import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PrintSharp from "@mui/icons-material/PrintSharp";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import {
    ProviderNonConformanceReportDetail,
    useNonConformanceReportDetail,
} from "../../api/nonconformance";
import {
    NonConformanceReport,
    formatNonConformanceReason,
} from "../../shared/types";
import { CircularProgress } from "@mui/material";
import { useApiService } from "../../shared/apiservice";
import { DownloadSharp } from "@mui/icons-material";
import AttachmentsGallery from "../../interface/shared/attachments-gallery";

export default function NonConformanceReportDetails() {
    const { id } = useParams<{ id: string }>();
    const reportId = id ? parseInt(id, 10) : undefined;

    return (
        <DashboardLayout>
            <ProviderNonConformanceReportDetail
                reportId={Number.isFinite(reportId) ? reportId : undefined}
            >
                <NonConformanceReportDetailsContent />
            </ProviderNonConformanceReportDetail>
        </DashboardLayout>
    );
}

function NonConformanceReportDetailsContent() {
    const navigate = useNavigate();
    const { report, errors, loading, fetchReport } = useNonConformanceReportDetail();
    const apiService = useApiService();
    const [printingReportId, setPrintingReportId] = useState<number | null>(null);
    const [downloadingReportId, setDownloadingReportId] = useState<number | null>(null);

    const printReport = async (r: NonConformanceReport) => {
        setPrintingReportId(r.id);
        try {
            const response = await apiService.get(`/non-conformance/${r.id}/report`);
            if (!response.ok) {
                window.alert("Failed to retrieve report. Please try again.");
                return;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            // Create a hidden iframe, load the PDF and trigger print
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = url;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } finally {
                    // Cleanup after a short delay to give the print dialog time to open
                    setTimeout(() => {
                        URL.revokeObjectURL(url);
                        if (document.body.contains(iframe)) document.body.removeChild(iframe);
                    }, 1000);
                }
            };
        } catch (err) {
            window.alert("Error printing report label. Please try again.");
        } finally {
            setPrintingReportId(null);
        }
    };

    const downloadReport = async (r: NonConformanceReport) => {
        setDownloadingReportId(r.id);
        try {
            const response = await apiService.get(`/non-conformance/${r.id}/report`);
            if (!response.ok) {
                window.alert("Failed to retrieve report. Please try again.");
                return;
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `NonConformance_${r.supplier}.pdf`;
            a.click();
        } catch (err) {
            window.alert("Error downloading report. Please try again.");
        } finally {
            setDownloadingReportId(null);
        }
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackRoundedIcon />}
                    onClick={() => navigate("/non-conformance")}
                >
                    Back
                </Button>

                {report && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={
                                downloadingReportId === report.id
                                    ? <CircularProgress size={24} />
                                    : <DownloadSharp />
                            }
                            onClick={() => downloadReport(report)}
                            disabled={downloadingReportId === report.id}
                        >
                            Download Report
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={
                                printingReportId === report.id
                                    ? <CircularProgress size={24} />
                                    : <PrintSharp />
                            }
                            onClick={() => printReport(report)}
                            disabled={printingReportId === report.id}
                        >
                            Print Report
                        </Button>
                    </Stack>
                )}
            </Stack>

            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchReport}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}

            {loading ? (
                <ReportDetailsSkeleton />
            ) : report ? (
                <StaggerContainer>
                    <StaggerItem>
                        <ReportSummary report={report} />
                    </StaggerItem>
                    {report.notes != null && report.notes.trim() !== "" && (
                        <StaggerItem>
                            <Card variant="outlined" sx={{ mt: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Notes
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {report.notes}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </StaggerItem>
                    )}
                    <StaggerItem>
                        <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                            Items
                        </Typography>
                        <ItemsTable items={report.items} />
                        <ItemAttachments items={report.items} />
                    </StaggerItem>
                </StaggerContainer>
            ) : null}
        </Box>
    );
}

function ReportSummary({ report }: { report: NonConformanceReport }) {
    const d = report.date;
    const dateStr = Number.isNaN(d.getTime())
        ? "—"
        : `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Report
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Date
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {dateStr}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Supplier
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {report.supplier}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Reference
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {report.reference}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Submitted by
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {report.user.name}
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function ItemsTable({ items }: { items: NonConformanceReport["items"] }) {
    if (items.length === 0) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="body2" color="text.secondary">
                        No line items on this report.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Notes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => (
                            <TableRow key={`${item.id}-${index}`}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={500}>
                                        {item.product.name}
                                    </Typography>
                                    {item.product.code ? (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {item.product.code}
                                        </Typography>
                                    ) : null}
                                </TableCell>
                                <TableCell>{formatNonConformanceReason(item.reason)}</TableCell>
                                <TableCell sx={{ maxWidth: 360 }}>
                                    {item.notes.trim() !== "" ? (
                                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                            {item.notes}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            —
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function ItemAttachments({ items }: { items: NonConformanceReport["items"] }) {
    const itemsWithAttachments = items.filter((i) => i.attachments.length > 0);
    if (itemsWithAttachments.length === 0) return null;

    return (
        <Stack spacing={2} sx={{ mt: 2 }}>
            {itemsWithAttachments.map((item) => (
                <Card key={item.id} variant="outlined">
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                            {item.product.name}
                        </Typography>
                        {item.product.code ? (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                {item.product.code}
                            </Typography>
                        ) : null}

                        <AttachmentsGallery attachments={item.attachments} />
                    </CardContent>
                </Card>
            ))}
        </Stack>
    );
}

function ReportDetailsSkeleton() {
    return (
        <Card variant="outlined">
            <CardContent>
                <Skeleton variant="text" width={160} height={28} sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                    {[0, 1, 2, 3].map((i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                            <Skeleton variant="text" width={64} height={16} />
                            <Skeleton variant="rounded" height={24} />
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
}
